<template>
  <div class="container page withdraw-page">
    <van-nav-bar :title="$t('withdraw.with_center') || 'Start Withdrawal'" class="custom-nav" :border="false">
      <template #left>
        <van-icon name="arrow-left" color="#e6b98a" size="24" @click="back()" />
      </template>
    </van-nav-bar>

    <div class="main-content">
      <!-- 余额背景横幅 -->
      <div class="balance-banner">
        <div class="balance-content">
          <span class="balance-val">{{ $money(userInfo.money || '0.00') }}</span>
          <span class="balance-label">{{$t("my.balance")}}</span>
        </div>
      </div>

      <!-- 提现表单卡片 -->
      <div class="form-card">
        <!-- 提现方式选择：银行卡 / 二维码快捷支付 / PayPay -->
        <div class="pay-type-row">
          <div class="pay-type" :class="{active: payType === 1}" @click="selectBankType()">
            <van-icon name="card" />
            <span>{{$t('withdraw.pay_bank')}}</span>
          </div>
          <div class="pay-type" :class="{active: payType === 2}" @click="selectQrcodeType()">
            <van-icon name="qr" />
            <span>{{$t('withdraw.pay_qrcode')}}</span>
          </div>
          <div class="pay-type" :class="{active: payType === 3}" @click="selectPaypayType()">
            <van-icon name="balance-pay" />
            <span>{{$t('withdraw.pay_paypay')}}</span>
          </div>
        </div>

        <!-- 二维码快捷支付：展示已绑定的收款码 -->
        <div class="qrcode-preview" v-if="payType === 2 && userInfo.qrcode_img">
          <img :src="userInfo.qrcode_img" />
          <span>{{$t('withdraw.qrcode_preview_tip')}}</span>
        </div>

        <!-- PayPay：展示已绑定的提款ID/手机号码 -->
        <div class="bank-row" v-if="payType === 3 && (userInfo.paypay_id || userInfo.paypay_phone)">
          <van-icon name="balance-pay" class="bank-icon" />
          <div class="bank-detail">
            <span class="bank-name">{{$t('withdraw.pay_paypay')}}</span>
            <span class="bank-num" v-if="userInfo.paypay_id">{{$t('setting.paypay_id')}}：{{userInfo.paypay_id}}</span>
            <span class="bank-num" v-if="userInfo.paypay_phone">{{$t('setting.paypay_phone')}}：{{userInfo.paypay_phone}}</span>
          </div>
          <van-icon name="checked" class="check-icon" />
        </div>

        <!-- 银行卡选择 -->
        <div class="bank-row" v-if="payType === 1 && bankInfo.bankinfo">
          <van-icon name="card" class="bank-icon" />
          <div class="bank-detail">
            <span class="bank-name">{{bankInfo.bankinfo}}</span>
            <span class="bank-num">{{bankInfo.fhnumber}}</span>
          </div>
          <van-icon name="checked" class="check-icon" />
        </div>

        <!-- 输入金额 -->
        <div class="input-area">
          <span v-if="$i18n.locale !== 'ja'" class="currency-symbol">{{ $currency() }}</span>
          <input 
            type="number" 
            v-model="withdraw_money" 
            class="money-input" 
            :placeholder="$t('q14') || 'Withdraw Amount'"
          />
          <span v-if="$i18n.locale === 'ja'" class="currency-symbol">{{ $currency() }}</span>
        </div>

        <!-- 限额提示 -->
        <div class="limit-tip">
          <van-icon name="warning-o" />
          <span>{{ $t('withdraw.limit_label') }}：{{ withdrawRole.min || 0 }} ~ {{ withdrawRole.max || 0 }}</span>
        </div>

        <!-- 确认按钮 -->
        <button class="confirm-btn" @click="doWithdraw()">
          {{$t('q18') || 'Submit'}}
        </button>
      </div>

      <!-- 通知说明（后台系统管理可编辑，四种语言） -->
      <div class="shuoming">
        <div class="l_p q1"><van-icon name="info-o" /> {{$t('q19') || 'Notice'}}：</div>
        <template v-if="noticeLines.length">
          <div class="l_p" v-for="(line, idx) in noticeLines" :key="idx">{{ idx + 1 }}、{{ line }}</div>
        </template>
        <template v-else>
          <div class="l_p">1、{{$t('q20')}}{{ withdrawRole.max || 0 }}{{$t("reservation.ge") || ''}}</div>
          <div class="l_p">2、{{$t('q21')}}{{ withdrawRole.min || 0 }}{{$t("reservation.ge") || ''}}</div>
          <div class="l_p">3、{{$t('q22')}}</div>
          <div class="l_p">4、{{$t('q23')}}</div>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  data() {
    return {
      withdraw_money: "",
      userInfo: {},
      withdrawRole: {},
      bankInfo: {},
      noticeConfig: {},
      payType: 1
    };
  },
  computed: {
    // 当前语言的提现通知内容（后台配置，按行拆分）
    noticeLines() {
      const lang = this.$i18n ? this.$i18n.locale : 'en';
      const text = this.noticeConfig[lang] || this.noticeConfig['en'] || '';
      return text.split('\n').map(s => s.trim()).filter(s => s.length > 0);
    }
  },
  methods: {
    getUserBankInfo(){
      this.$http({
        method: 'get',
        url: 'user_get_bank'
      }).then(res=>{
        if(res.code === 200){
          this.bankInfo = res.data.info || {};
          this.syncDefaultPayType();
        }else if(res.code ===401){
          this.$toast(res.msg);
        }
      })
    },
    // 按绑定情况自动选择提现方式：当前选中方式可用则保持，否则按 银行卡→收款码→PayPay 回退（任一绑定即可提款）
    syncDefaultPayType(){
      const hasBank = !!(this.bankInfo && this.bankInfo.bankinfo);
      const hasQr = !!this.userInfo.qrcode_img;
      const hasPp = !!(this.userInfo.paypay_id || this.userInfo.paypay_phone);
      if (this.payType === 1 && hasBank) return;
      if (this.payType === 2 && hasQr) return;
      if (this.payType === 3 && hasPp) return;
      if (hasBank) { this.payType = 1; }
      else if (hasQr) { this.payType = 2; }
      else if (hasPp) { this.payType = 3; }
    },
    // 选择银行卡支付：未绑卡时引导去绑定银行卡页，已绑卡直接选中可提交
    selectBankType(){
      if (!this.bankInfo || !this.bankInfo.bankinfo) {
        this.$toast(this.$t('withdraw.no_bank'));
        this.$router.push({path: '/BindCard'});
        return;
      }
      this.payType = 1;
    },
    // 选择二维码快捷支付：未上传收款码时引导去银行卡设置页上传
    selectQrcodeType(){
      if (!this.userInfo.qrcode_img) {
        this.$toast(this.$t('withdraw.no_qrcode'));
        this.$router.push({path: '/Setbank'});
        return;
      }
      this.payType = 2;
    },
    // 选择 PayPay：未绑定提款ID/手机号码时引导去银行卡信息页绑定
    selectPaypayType(){
      if (!this.userInfo.paypay_id && !this.userInfo.paypay_phone) {
        this.$toast(this.$t('withdraw.no_paypay'));
        this.$router.push({path: '/Setbank'});
        return;
      }
      this.payType = 3;
    },
    back(){
      return window.history.back();
    },
    getUserInfo(){
      this.$http({
        method: 'get',
        url: 'user_info'
      }).then(res=>{
        if(res.code === 200){
          this.userInfo = res.data;
          this.syncDefaultPayType();
        }else if(res.code ===401){
          this.$toast(res.msg);
        }
      })
    },
    // 获取提现规则（最低/最高金额、每日次数，来自后台基本配置）
    getUserWithdrawRole(){
      this.$http({
        method: 'get',
        url: 'user_get_withdraw_role'
      }).then(res=>{
        if(res.code === 200){
          this.withdrawRole = res.data;
        }else if(res.code ===401){
          this.$toast(res.msg);
        }
      })
    },
    // 获取后台配置的提现通知（四种语言）
    getWithdrawNotice(){
      this.$http({
        method: 'get',
        url: 'withdraw_notice'
      }).then(res=>{
        if(res.code === 200 && res.data){
          this.noticeConfig = res.data;
        }
      })
    },
    doWithdraw(){
      if(!this.withdraw_money || this.withdraw_money <= 0){
        this.$toast(this.$t("setting.correct_money"));
        return false;
      }

      // 验证提现条件（限额来自后台配置）
      const amount = parseFloat(this.withdraw_money);
      const minAmount = parseFloat(this.withdrawRole.min || 0);
      const maxAmount = parseFloat(this.withdrawRole.max || 0);
      const balance = parseFloat(this.userInfo.money || 0);

      // 订单任务完成度校验（与后端口径一致）：只有「没有订单任务」或「已完成当前等级任务一轮」才允许申请提款；
      // 轮次进行中（0<已完成<总数）前端先行拦截，存在未付款订单的进行中状态由后端兜底拒绝
      const cycleInfo = this.userInfo.daily_order_limit || {};
      const doneCount = parseInt(cycleInfo.today_completed || 0, 10);
      const totalCount = parseInt(cycleInfo.daily_limit || 0, 10);
      if (totalCount > 0 && doneCount > 0 && doneCount < totalCount) {
        this.$toast(this.$t('withdraw.task_not_done'));
        return false;
      }

      // 全额提清余额不受最低提款限额（与后端口径一致：任何会员都可以申请提款至账户余额）
      if (minAmount > 0 && amount < minAmount && amount < balance) {
        this.$toast(`${this.$t('withdraw.min_amount_error')} ${minAmount}`);
        return false;
      }

      if (maxAmount > 0 && amount > maxAmount) {
        this.$toast(`${this.$t('withdraw.max_amount_error')} ${maxAmount}`);
        return false;
      }

      // 申请提款金额必须小于或等于账号余额（仅大于余额时拦截，等于余额允许全额提清，四语言 amount_must_lt_balance）
      if (amount > balance) {
        this.$toast(this.$t('withdraw.amount_must_lt_balance'));
        return false;
      }

      // 银行卡方式必须先绑定银行卡，否则引导去绑定页
      if (this.payType === 1 && (!this.bankInfo || !this.bankInfo.bankinfo)) {
        this.$toast(this.$t('withdraw.no_bank'));
        this.$router.push({path: '/BindCard'});
        return false;
      }

      // PayPay 方式必须先绑定提款ID或手机号码，否则引导去绑定页
      if (this.payType === 3 && !this.userInfo.paypay_id && !this.userInfo.paypay_phone) {
        this.$toast(this.$t('withdraw.no_paypay'));
        this.$router.push({path: '/Setbank'});
        return false;
      }

      this.$http({
        method: 'post',
        data: { money: this.withdraw_money, pay_type: this.payType },
        url: 'user_set_withdraw'
      }).then(res=>{
        if(res.code === 200){
          this.$toast(res.msg);
          this.getUserInfo();
          this.getUserWithdrawRole();
          this.withdraw_money = '';
        }else if(res.code ===401){
          this.$toast(res.msg);
        }
      })
    }
  },
  created() {
    if(!localStorage.getItem('token')){
      this.$router.push({path:'/Login'})
    }else {
      this.getUserInfo();
      this.getUserWithdrawRole();
      this.getWithdrawNotice();
    }
  },
  mounted(){
    this.getUserBankInfo();
  }
};
</script>

<style lang='less' scoped>
@import "../../assets/css/base.css";

.withdraw-page {
  background-color: #1f1c1f;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}

/* 导航栏暗金风格 */
.custom-nav {
  background-color: #1f1c1f !important;
  position: relative;
  z-index: 10;
}
::v-deep .custom-nav .van-nav-bar__title {
  color: #e6b98a !important;
  font-size: 36px;
  font-weight: 600;
}
::v-deep .van-hairline--bottom::after {
  border-bottom-width: 0 !important;
}

/* Balance Banner */
.balance-banner {
  background: linear-gradient(120deg, #e2bb91, #d4a572);
  padding: 48px 60px 80px;
  box-sizing: border-box;
}

.balance-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  color: #3a2415;
  
  .balance-val {
    font-size: 48px;
    font-weight: bold;
  }
  
  .balance-label {
    font-size: 32px;
    font-weight: bold;
    opacity: 0.85;
  }
}

/* Main Content */
.main-content {
  flex: 1;
  padding-bottom: 60px;
}

.form-card {
  background-color: #212025;
  border-radius: 40px 40px 0 0;
  margin-top: -40px;
  padding: 60px 40px 48px;
  position: relative;
  z-index: 5;
}

/* 提现方式选择 */
.pay-type-row {
  display: flex;
  gap: 20px;
  margin-bottom: 40px;

  .pay-type {
    /* 两按钮严格等宽，长文案自动换行不变形 */
    flex: 1 1 0;
    min-width: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    /* 银行卡与上传收款二维码统一金棕风格，字体/间距自适应 */
    background: linear-gradient(135deg, #f5d6a9, #e6b98a);
    border: 2px solid #766350;
    border-radius: 16px;
    padding: 24px 12px;
    color: #4a3a2e;
    font-size: 26px;
    font-weight: 600;
    letter-spacing: 1px;
    line-height: 1.3;
    text-align: center;
    cursor: pointer;
    transition: all 0.2s;

    .van-icon {
      font-size: 36px;
      margin-right: 12px;
      flex-shrink: 0;
    }

    /* 选中态：加深边框与底色突出当前方式 */
    &.active {
      border-color: #a97e4f;
      background: linear-gradient(135deg, #f8e0bd, #edc494);
      box-shadow: 0 4px 14px rgba(230, 185, 138, 0.35);
    }
  }
}

/* 二维码快捷支付预览 */
.qrcode-preview {
  display: flex;
  flex-direction: column;
  align-items: center;
  background: linear-gradient(135deg, #4a3c31, #2b2b2b);
  border: 2px solid #766350;
  border-radius: 16px;
  padding: 30px 24px;
  margin-bottom: 48px;

  img {
    width: 300px;
    border-radius: 12px;
    background: #fff;
  }

  span {
    margin-top: 16px;
    color: #999;
    font-size: 24px;
  }
}

/* 银行卡选择 */
.bank-row {
  display: flex;
  align-items: center;
  background: linear-gradient(135deg, #4a3c31, #2b2b2b);
  border: 2px solid #766350;
  border-radius: 16px;
  padding: 30px 24px;
  margin-bottom: 48px;
  
  .bank-icon {
    font-size: 48px;
    color: #e6b98a;
    margin-right: 20px;
  }
  
  .bank-detail {
    flex: 1;
    display: flex;
    flex-direction: column;
    
    .bank-name {
      color: #fff;
      font-size: 32px;
      font-weight: bold;
      margin-bottom: 8px;
    }
    .bank-num {
      color: #999;
      font-size: 28px;
      font-family: monospace;
    }
  }
  
  .check-icon {
    font-size: 40px;
    color: #07c160;
  }
}

/* Input Area */
.input-area {
  display: flex;
  align-items: center;
  border-bottom: 2px solid #333;
  padding-bottom: 20px;
  margin-bottom: 24px;
  transition: border-color 0.2s;

  &:focus-within {
    border-bottom-color: #e6b98a;
  }
  
  .currency-symbol {
    color: #df9452;
    font-size: 48px;
    margin-right: 20px;
    font-weight: 500;
  }
  
  .money-input {
    flex: 1;
    background: transparent;
    border: none;
    color: #fff;
    font-size: 40px;
    outline: none;
    
    &::placeholder {
      color: #555;
      font-size: 32px;
    }
  }
}

/* 限额提示 */
.limit-tip {
  display: flex;
  align-items: center;
  color: #8a7a66;
  font-size: 24px;
  margin-bottom: 48px;

  .van-icon {
    margin-right: 8px;
    font-size: 26px;
    color: #df9452;
  }
}

/* Confirm Button */
.confirm-btn {
  width: 100%;
  height: 100px;
  background: linear-gradient(to right, #efcba7, #dcab76);
  border: none;
  border-radius: 16px;
  color: #68302c;
  font-size: 36px;
  font-weight: bold;
  cursor: pointer;
  transition: all 0.2s;
  box-shadow: 0 8px 24px rgba(220, 171, 118, 0.25);
  
  &:active {
    opacity: 0.9;
    transform: translateY(2px);
  }
}

/* 通知说明 */
.shuoming {
  background: #242424;
  color: #999;
  padding: 40px;
  border-radius: 20px;
  margin: 40px 40px 0;
  border: 1px solid #333;
  
  .l_p {
    font-size: 26px;
    margin-top: 20px;
    line-height: 1.6;
    word-break: break-word;
    
    &.q1 {
      font-size: 30px;
      color: #e6b98a;
      margin-top: 0;
      margin-bottom: 10px;
      font-weight: bold;
      display: flex;
      align-items: center;
      
      .van-icon {
        margin-right: 10px;
        font-size: 36px;
      }
    }
  }
}
</style>
